/**
 * Provider Information Modal - Eosera Shopify Integration
 * 
 * This script enhances the existing NPI verification system with
 * the new provider segmentation requirements for Klaviyo.
 * 
 * Integrates with: product-multiple-variant-picker.liquid
 */

// Enhanced Provider Modal for Eosera
class EoseraProviderModal {
    static callback = null;
    static initialized = false;
    static modalHTML = null;

    // Initialize the enhanced modal
    static init() {
        if (this.initialized) return;

        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.init());
            return;
        }

        this.injectStyles();
        this.enhanceExistingModal();
        this.initialized = true;
    }

    // Inject enhanced styles
    static injectStyles() {
        if (document.getElementById('eosera-provider-styles')) return;

        const styles = `
        <style id="eosera-provider-styles">
        /* Enhanced styles for existing NPI modal */
        .provider-type-section {
            margin: 20px 0;
        }
        
        .provider-type-section h4 {
            color: #333;
            margin: 0 0 15px 0;
            font-size: 16px;
            font-weight: 600;
        }
        
        .provider-type-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 10px;
            margin-top: 15px;
        }
        
        .provider-type-option {
            display: flex;
            align-items: center;
            padding: 12px 16px;
            border: 2px solid #e1e5e9;
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.3s ease;
            background: white;
            position: relative;
        }
        
        .provider-type-option:hover {
            border-color: #592d8b;
            background: #f8f6ff;
        }
        
        .provider-type-option.selected {
            border-color: #592d8b;
            background: #592d8b;
            color: white;
        }
        
        .provider-type-option input[type="checkbox"] {
            position: absolute;
            opacity: 0;
            cursor: pointer;
        }
        
        .provider-type-option label {
            margin: 0;
            cursor: pointer;
            font-weight: 500;
            flex: 1;
            font-size: 14px;
            position: relative;
            padding-left: 28px;
        }
        
        .provider-type-option label:before {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            width: 18px;
            height: 18px;
            border: 2px solid #d1d5db;
            border-radius: 4px;
            background: white;
            transition: all 0.3s ease;
        }
        
        .provider-type-option.selected label:before {
            background: white;
            border-color: white;
        }
        
        .provider-type-option label:after {
            content: '✓';
            position: absolute;
            left: 3px;
            top: 50%;
            transform: translateY(-50%);
            color: #592d8b;
            font-size: 12px;
            font-weight: bold;
            opacity: 0;
            transition: all 0.3s ease;
        }
        
        .provider-type-option.selected label:after {
            opacity: 1;
        }
        
        .samples-section {
            margin: 20px 0;
            padding: 15px;
            background: #f8f9ff;
            border-radius: 8px;
            border: 1px solid #e1e5e9;
        }
        
        .samples-section h4 {
            color: #333;
            margin: 0 0 15px 0;
            font-size: 16px;
            font-weight: 600;
        }
        
        .samples-options {
            display: flex;
            gap: 15px;
            flex-wrap: wrap;
        }
        
        .sample-option {
            display: flex;
            align-items: center;
            padding: 8px 15px;
            border: 2px solid #e1e5e9;
            border-radius: 20px;
            cursor: pointer;
            transition: all 0.3s;
            background: white;
        }
        
        .sample-option:hover {
            border-color: #592d8b;
        }
        
        .sample-option.selected {
            border-color: #592d8b;
            background: #592d8b;
            color: white;
        }
        
        .sample-option input[type="radio"] {
            margin-right: 8px;
        }
        
        .products-section {
            margin: 20px 0;
        }
        
        .products-section h4 {
            color: #333;
            margin: 0 0 15px 0;
            font-size: 16px;
            font-weight: 600;
        }
        
        .products-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
            gap: 10px;
        }
        
        .product-interest-option {
            display: flex;
            align-items: center;
            padding: 12px;
            border: 2px solid #e1e5e9;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.3s;
            background: white;
        }
        
        .product-interest-option:hover {
            border-color: #592d8b;
            background: #f8f6ff;
        }
        
        .product-interest-option.selected {
            border-color: #592d8b;
            background: #592d8b;
            color: white;
        }
        
        .product-interest-option input[type="checkbox"] {
            margin-right: 8px;
            transform: scale(1.1);
        }
        
        .product-interest-option label {
            margin: 0;
            cursor: pointer;
            font-weight: 500;
            font-size: 14px;
        }
        
        .npi-validation {
            margin-top: 5px;
            font-size: 12px;
        }
        
        .validation-error {
            color: #e74c3c;
            font-size: 12px;
            margin-top: 5px;
            display: none;
        }
        
        /* Enhanced submit button */
        .enhanced-submit-btn {
            border-radius: 8px !important;
        }
        
        .enhanced-submit-btn:hover {
            transform: translateY(-2px) !important;
            box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3) !important;
        }
        
        .enhanced-submit-btn:disabled {
            opacity: 0.6 !important;
            cursor: not-allowed !important;
            transform: none !important;
        }
        
        @media (max-width: 600px) {
            .provider-type-grid,
            .products-grid {
                grid-template-columns: 1fr;
            }
            
            .samples-options {
                flex-direction: column;
            }
        }
        </style>
        `;

        document.head.insertAdjacentHTML('beforeend', styles);
    }

    // Enhance the existing NPI modal
    static enhanceExistingModal() {
        const existingModal = document.getElementById('npi_verification_lightbox');
        if (!existingModal) {
            console.warn('Existing NPI modal not found');
            return;
        }

        const form = existingModal.querySelector('#npi_verification_form');
        if (!form) {
            console.warn('NPI form not found');
            return;
        }

        console.log('✅ EoseraProviderModal: Enhancing existing modal');

        // Add NPI validation to existing input
        this.enhanceNPIInput();

        // Enhance submit button and override form submission
        this.enhanceSubmitButton(form);
        this.overrideFormSubmission(form);
        
        // Note: Static modal is already configured with correct options
        // The JavaScript handles form processing and validation
        
        console.log('✅ EoseraProviderModal: Modal enhancement complete');
    }

    // Enhance NPI input with validation
    static enhanceNPIInput() {
        const npiInput = document.getElementById('npi_number');
        if (!npiInput) return;

        // Add validation container
        const validationDiv = document.createElement('div');
        validationDiv.id = 'npi-validation';
        validationDiv.className = 'npi-validation';
        npiInput.parentNode.insertBefore(validationDiv, npiInput.nextSibling);
    }

    // Add provider type section
    static addProviderTypeSection(form) {
        // Remove existing practice type section
        const existingPracticeTypes = form.querySelector('.practice-type-options');
        if (existingPracticeTypes) {
            existingPracticeTypes.parentElement.remove();
        }

        const providerSection = document.createElement('div');
        providerSection.className = 'provider-type-section';
        providerSection.innerHTML = `
            <h4>What type of healthcare provider are you? <span style="color: #e74c3c;">*</span></h4>
            <p style="margin: 0 0 15px 0; color: #666; font-size: 14px;">Select all that apply (minimum 1 required)</p>
            <div class="provider-type-grid">
                <div class="provider-type-option" onclick="EoseraProviderModal.toggleProviderType('GP')">
                    <input type="checkbox" id="provider_GP" name="practice_type" value="GP">
                    <label for="provider_GP">General Practice</label>
                </div>
                <div class="provider-type-option" onclick="EoseraProviderModal.toggleProviderType('PEDI')">
                    <input type="checkbox" id="provider_PEDI" name="practice_type" value="PEDI">
                    <label for="provider_PEDI">Pediatrician</label>
                </div>
                <div class="provider-type-option" onclick="EoseraProviderModal.toggleProviderType('ENT')">
                    <input type="checkbox" id="provider_ENT" name="practice_type" value="ENT">
                    <label for="provider_ENT">Ear, Nose, and Throat</label>
                </div>
                <div class="provider-type-option" onclick="EoseraProviderModal.toggleProviderType('AUDS')">
                    <input type="checkbox" id="provider_AUDS" name="practice_type" value="AUDS">
                    <label for="provider_AUDS">Audiologist</label>
                </div>
                <div class="provider-type-option" onclick="EoseraProviderModal.toggleProviderType('Other')">
                    <input type="checkbox" id="provider_Other" name="practice_type" value="Other">
                    <label for="provider_Other">Other</label>
                </div>
            </div>
            <div class="validation-error" id="practice-type-error-static">
                Please select at least one practice type
            </div>
        `;

        // Insert after NPI input section
        const npiSection = form.querySelector('.row.mb-3.mt-4');
        npiSection.parentNode.insertBefore(providerSection, npiSection.nextSibling);
    }

    // Add samples section
    static addSamplesSection(form) {
        const samplesSection = document.createElement('div');
        samplesSection.className = 'samples-section';
        samplesSection.innerHTML = `
            <h4>Sample Preferences</h4>
            <p style="margin: 0 0 15px 0; color: #666; font-size: 14px;">Would you like to receive product samples?</p>
            <div class="samples-options">
                <div class="sample-option" onclick="EoseraProviderModal.selectSample('Yes')">
                    <input type="radio" id="samples_yes" name="samples_requested" value="Yes">
                    <label for="samples_yes">Yes, send me samples</label>
                </div>
                <div class="sample-option" onclick="EoseraProviderModal.selectSample('No')">
                    <input type="radio" id="samples_no" name="samples_requested" value="No">
                    <label for="samples_no">No samples needed</label>
                </div>
            </div>
        `;

        // Insert before practice name section
        const practiceNameSection = form.querySelector('.row.mb-3:last-of-type');
        practiceNameSection.parentNode.insertBefore(samplesSection, practiceNameSection);
    }

    // Add products section
    static addProductsSection(form) {
        const productsSection = document.createElement('div');
        productsSection.className = 'products-section';
        productsSection.innerHTML = `
            <h4>Product Selection</h4>
            <p style="margin: 0 0 15px 0; color: #666; font-size: 14px;">Products will be selected on the next page based on your needs.</p>
        `;

        // Insert before practice name section
        const practiceNameSection = form.querySelector('.row.mb-3:last-of-type');
        practiceNameSection.parentNode.insertBefore(productsSection, practiceNameSection);
    }

    // Enhance submit button
    static enhanceSubmitButton(form) {
        const submitBtn = form.querySelector('button[type="submit"]');
        if (submitBtn) {
            submitBtn.classList.add('enhanced-submit-btn');
        }
    }

    // Override form submission
    static overrideFormSubmission(form) {
        form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleEnhancedSubmission(form);
        });
    }

    // Handle enhanced form submission
    static handleEnhancedSubmission(form) {
        // Hide previous errors
        document.querySelectorAll('.validation-error').forEach(error => {
            error.style.display = 'none';
        });

        const formData = new FormData(form);
        
        // Validate required fields
        const npiNumber = formData.get('npi_number');
        const practiceName = formData.get('practice_name');
        const email = formData.get('email') || formData.get('provider_email');
        
        // Get selected provider types from static modal
        const selectedProviderTypes = [];
        document.querySelectorAll('input[name="practice_type"]:checked').forEach(checkbox => {
            selectedProviderTypes.push(checkbox.value);
        });
        
        console.log('🔍 Selected provider types:', selectedProviderTypes);
        
        // Basic validation - format only
        // Real NPI validation is done via CMS API: https://npiregistry.cms.hhs.gov/api/
        let hasErrors = false;
        
        if (!npiNumber) {
            alert('Please enter your NPI number');
            hasErrors = true;
        } else if (!/^\d{10}$/.test(npiNumber)) {
            alert('Please enter a valid 10-digit NPI number');
            hasErrors = true;
        }
        
        if (selectedProviderTypes.length === 0) {
            console.log('❌ No practice types selected');
            const errorDiv = document.getElementById('practice-type-error-static');
            if (errorDiv) {
                errorDiv.style.display = 'block';
                console.log('📢 Showing error message');
            } else {
                console.log('⚠️ Error div not found');
            }
            hasErrors = true;
        } else {
            console.log('✅ Practice types selected:', selectedProviderTypes.length);
        }
        
        if (!practiceName) {
            alert('Please enter your practice name');
            hasErrors = true;
        }
        
        // Email is optional - if not provided, system will use NPI-based fallback
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            alert('Please enter a valid email address');
            hasErrors = true;
        }
        
        if (hasErrors) {
            return;
        }
        
        // Collect enhanced data (with fallbacks for missing elements)
        const enhancedData = {
            npi_number: npiNumber,
            practice_name: practiceName,
            provider_type: selectedProviderTypes.join(', '), // Join multiple selections
            samples_requested: formData.get('samples_requested') || 'No',

        };

        // Add email only if it's provided and valid
        if (email && email.trim()) {
            enhancedData.email = email.trim();
        }

        console.log('Enhanced Provider Data:', enhancedData);

        // Call the original submission with enhanced data
        this.submitToAPI(enhancedData);
    }

    // Submit to API with enhanced data
    static async submitToAPI(data) {
        console.log('🚀 Starting API submission with data:', data);
        
        const submitBtn = document.querySelector('.enhanced-submit-btn');
        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.textContent = 'Verifying NPI...';
        }

        try {
            // Step 1: Verify NPI number
            console.log('🔍 Step 1: Verifying NPI number...', data.npi_number);
            console.log('📡 Making request to: https://api.icepick.co/npiregistry/verify-npi.php');
            
            const npiResponse = await fetch('https://api.icepick.co/npiregistry/verify-npi.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    npi: data.npi_number,
                    practice_type: data.provider_type,
                    practice_name: data.practice_name
                })
            });

            console.log('📨 NPI Response status:', npiResponse.status, npiResponse.statusText);
            
            if (!npiResponse.ok) {
                throw new Error(`NPI API request failed: ${npiResponse.status} ${npiResponse.statusText}`);
            }
            
            const npiResult = await npiResponse.json();
            console.log('✅ NPI verification result:', npiResult);

            // Check if NPI verification was successful
            if (npiResult.result_count > 0 && npiResult.results && npiResult.results.length > 0) {
                console.log('✅ NPI verified successfully');
                
                // Update button text for second step
                if (submitBtn) {
                    submitBtn.textContent = 'Saving to Klaviyo...';
                }

                // Step 2: Store provider data for later use in checkout
                console.log('💾 Step 2: Storing provider data for checkout...');
                
                // Store enhanced data for later use in checkout
                localStorage.setItem('eoseraProviderData', JSON.stringify(data));
                
                // Dispatch custom event
                window.dispatchEvent(new CustomEvent('eoseraProviderDataCollected', {
                    detail: { 
                        ...data, 
                        npi_verified: true 
                    }
                }));

                console.log('✅ Provider data stored successfully');
                
                // Close modal and continue with cart flow
                this.closeModal();
                
                // Trigger the cart addition flow
                this.proceedWithCartAddition(data);
            } else {
                throw new Error('Invalid NPI number. Please verify and try again.');
            }
        } catch (error) {
            console.error('❌ Provider verification error:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                name: error.name
            });
            
            // More specific error handling based on the actual error
            if (error.message.includes('NPI') || error.message.includes('Invalid NPI')) {
                alert('Error verifying NPI number. Please check the number and try again.');
            } else if (error.message.includes('fetch') || error.name === 'TypeError') {
                console.warn('⚠️ Network/API error, but allowing cart addition to proceed');
                
                // If it's a network error, proceed with cart addition 
                // This handles CORS, 404, or other API connectivity issues
                alert('There was a connectivity issue, but your samples will be added to the cart. You can complete your request on the checkout page.');
                
                localStorage.setItem('eoseraProviderData', JSON.stringify(data));
                this.closeModal();
                this.proceedWithCartAddition(data);
            } else if (error.message.includes('Klaviyo')) {
                console.warn('⚠️ Klaviyo error, but allowing cart addition to proceed');
                alert('Information saved but there was an issue with data processing. You can still proceed with your order.');
                
                localStorage.setItem('eoseraProviderData', JSON.stringify(data));
                this.closeModal();
                this.proceedWithCartAddition(data);
            } else {
                console.warn('⚠️ Unknown error, but allowing cart addition to proceed as fallback');
                console.log('Proceeding with cart addition despite error for better UX');
                
                // As a fallback for any other error, still allow cart addition
                // This ensures the user experience isn't completely broken
                localStorage.setItem('eoseraProviderData', JSON.stringify(data));
                this.closeModal();
                this.proceedWithCartAddition(data);
            }
        } finally {
            if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.textContent = 'Submit';
            }
        }
    }

    // Close modal
    static closeModal() {
        const modal = document.getElementById('npi_verification_lightbox');
        if (modal) {
            modal.style.display = 'none';
        }
    }

    // Proceed with sample cart addition after successful NPI verification
    static proceedWithCartAddition(providerData) {
        console.log('🛒 Adding products to sample cart with provider data:', providerData);
        
        // Get selected variants from checkboxes (for multiple variant selection)
        const selectedVariants = this.getSelectedVariants();
        
        if (selectedVariants.length === 0) {
            console.error('❌ No variants selected');
            alert('Please select at least one product variant before proceeding.');
            return;
        }

        console.log('📦 Selected variants:', selectedVariants);

        // Get basic product info
        const productTitle = document.querySelector('.product__title, .product-title, h1')?.textContent?.trim() || 'Sample Product';
        const productImage = this.getProductImage();

        // Add each selected variant to the sample cart
        const addedProducts = [];
        
        selectedVariants.forEach(variant => {
            const productData = {
                id: variant.id,
                product_id: variant.product_id || 'sample-product',
                variant_id: variant.id,
                title: productTitle,
                variant_title: variant.title,
                image: productImage,
                quantity: 1,
                price: 0, // Samples are free
                properties: {
                    npi_number: providerData.npi_number,
                    practice_name: providerData.practice_name,
                    provider_type: providerData.provider_type,
                    samples_requested: providerData.samples_requested,
                    verified_provider: 'true'
                }
            };

            // Add to sample cart
            if (window.EoseraSampleCheckout && window.EoseraSampleCheckout.addToSampleCart) {
                window.EoseraSampleCheckout.addToSampleCart(productData);
                addedProducts.push(productData);
                console.log('✅ Added to sample cart:', productData.variant_title);
            } else {
                console.error('❌ EoseraSampleCheckout not available');
            }
        });

        if (addedProducts.length > 0) {
            // Show success message for all added products
            this.showSampleAddedMessage({
                title: `${addedProducts.length} sample${addedProducts.length > 1 ? 's' : ''}`,
                variant_title: addedProducts.map(p => p.variant_title).join(', ')
            });
            
            // Check URL for selling_plan parameter before redirect
            const urlParams = new URLSearchParams(window.location.search);
            const sellingPlanId = urlParams.get('selling_plan');
            
            // Handle subscription data based on URL parameter
            if (sellingPlanId && sellingPlanId !== '') {
                // URL has selling_plan parameter - save subscription data
                const subscriptionData = {
                    selling_plan_id: sellingPlanId,
                    is_subscription: true,
                    frequency_name: this.getSubscriptionFrequencyName(sellingPlanId),
                    timestamp: new Date().toISOString(),
                    url: window.location.href
                };
                
                try {
                    localStorage.setItem('eosera_subscription_plan', JSON.stringify(subscriptionData));
                    console.log('💾 Subscription plan saved from URL:', subscriptionData);
                } catch (error) {
                    console.error('❌ Failed to save subscription data:', error);
                }
            } else {
                // No selling_plan parameter - clear any existing subscription data (One Time Order)
                try {
                    localStorage.removeItem('eosera_subscription_plan');
                    console.log('🧹 One Time Order confirmed - cleared subscription data from localStorage');
                } catch (error) {
                    console.error('❌ Failed to clear subscription data:', error);
                }
            }
            
            // Redirect to sample checkout after a brief delay
            setTimeout(() => {
                if (window.EoseraSampleCheckout && window.EoseraSampleCheckout.redirectToCheckout) {
                    window.EoseraSampleCheckout.redirectToCheckout();
                } else {
                    window.location.href = '/pages/request-samples';
                }
            }, 1500);
        } else {
            console.error('❌ No products were added to cart');
            alert('Error: Could not add products to sample cart. Please try again.');
        }
    }

    // Get user-friendly subscription frequency name
    static getSubscriptionFrequencyName(sellingPlanId) {
        const frequencyMap = {
            '7060029728': 'Monthly Subscription',
            '7060062496': 'Quarterly Subscription', 
            '7060095264': 'Semiannual Subscription'
        };
        
        return frequencyMap[sellingPlanId] || (sellingPlanId ? 'Subscription' : 'One-Time Order');
    }

    // Get selected variants from checkboxes
    static getSelectedVariants() {
        const selectedVariants = [];
        
        // Method 1: Get from checked checkboxes with data-variant-id
        const checkedBoxes = document.querySelectorAll('input[data-variant-id]:checked');
        checkedBoxes.forEach(checkbox => {
            const variantId = checkbox.getAttribute('data-variant-id');
            const label = checkbox.closest('label')?.querySelector('.product_form_input_pill_label');
            const variantTitle = label?.textContent?.trim() || 'Sample Variant';
            
            if (variantId) {
                selectedVariants.push({
                    id: variantId,
                    title: variantTitle,
                    product_id: 'sample-product'
                });
            }
        });
        
        // Method 2: Get from window.product_variations if available
        if (selectedVariants.length === 0 && window.product_variations) {
            // Try to find selected variants from global data
            const checkboxes = document.querySelectorAll('input[type="checkbox"]:checked');
            checkboxes.forEach(checkbox => {
                const variantId = checkbox.value || checkbox.getAttribute('value');
                if (variantId && window.product_variations) {
                    const variantData = window.product_variations.find(v => v.id == variantId);
                    if (variantData) {
                        selectedVariants.push({
                            id: variantData.id,
                            title: variantData.title || variantData.name || 'Sample Variant',
                            product_id: 'sample-product'
                        });
                    }
                }
            });
        }
        
        // Method 3: Fallback - look for any checked inputs with numeric values (likely variant IDs)
        if (selectedVariants.length === 0) {
            const allChecked = document.querySelectorAll('input:checked');
            allChecked.forEach(input => {
                const value = input.value;
                // Check if value looks like a Shopify variant ID (numeric, usually 10+ digits)
                if (/^\d{10,}$/.test(value)) {
                    const variantTitle = input.getAttribute('data-title') || 
                                      input.closest('label')?.textContent?.trim() || 
                                      'Sample Variant';
                                      
                    selectedVariants.push({
                        id: value,
                        title: variantTitle,
                        product_id: 'sample-product'
                    });
                }
            });
        }
        
        console.log('🔍 Found selected variants:', selectedVariants);
        return selectedVariants;
    }

    // Extract product data from form
    static extractProductDataFromForm(formData, productForm) {
        // Get basic product info
        const productTitle = document.querySelector('.product__title, .product-title, h1')?.textContent?.trim() || 'Sample Product';
        const productId = this.extractProductIdFromAction(productForm.action);
        
        // Get variant ID from form data or hidden input
        const variantId = formData.get('id') || document.querySelector('input[name="id"]')?.value;
        if (!variantId) {
            console.warn('No variant ID found');
            return null;
        }
        
        const quantity = parseInt(formData.get('quantity')) || 1;
        
        // Get variant title using multiple methods
        let variantTitle = '';
        
        // Method 1: From window.product_variations data
        if (window.product_variations && Array.isArray(window.product_variations)) {
            const variantData = window.product_variations.find(v => v.id == variantId);
            if (variantData) {
                variantTitle = variantData.title || variantData.name || '';
            }
        }
        
        // Method 2: From selected checkboxes
        if (!variantTitle) {
            const selectedCheckbox = document.querySelector(`input[data-variant-id="${variantId}"]:checked`);
            if (selectedCheckbox) {
                const labelElement = selectedCheckbox.closest('label')?.querySelector('.product_form_input_pill_label');
                if (labelElement) {
                    variantTitle = labelElement.textContent.trim();
                }
            }
        }
        
        // Method 3: From form selects
        if (!variantTitle) {
            variantTitle = this.getVariantTitle(productForm);
        }
        
        // Get product image
        const productImage = this.getProductImage();
        
        return {
            id: variantId, // Use actual variant ID, not Date.now()
            product_id: productId,
            variant_id: variantId,
            title: productTitle,
            variant_title: variantTitle || 'Sample Variant',
            image: productImage,
            quantity: quantity,
            price: 0, // Samples are free
            properties: {}
        };
    }

    // Extract product ID from form action URL
    static extractProductIdFromAction(action) {
        const match = action.match(/\/products\/([^\/]+)/);
        return match ? match[1] : null;
    }

    // Get variant title from selected options
    static getVariantTitle(form) {
        const selects = form.querySelectorAll('select[name*="option"], select[name*="variant"]');
        const titles = [];
        
        selects.forEach(select => {
            if (select.value && select.value !== '') {
                const option = select.options[select.selectedIndex];
                if (option && option.text !== 'Select' && option.text !== '') {
                    titles.push(option.text);
                }
            }
        });
        
        return titles.join(' / ');
    }

    // Get product image
    static getProductImage() {
        const imageSelectors = [
            '.product_gallery_slider .swiper-slide img',
            '.product-image img',
            '.product__media img',
            '.product__media .media img',
            '.product-gallery img',
            '.featured-image img',
            'img[data-product-image]',
            'main img[src*="product"]',
            'img[alt*="product"]',
            'img[class*="product"]'
        ];
        
        for (const selector of imageSelectors) {
            const img = document.querySelector(selector);
            if (img && img.src && !img.src.includes('placeholder') && !img.src.includes('loading')) {
                return img.src;
            }
        }
        
        return '';
    }

    // Show sample added message
    static showSampleAddedMessage(productData) {
        // Create and show a temporary success message
        const message = document.createElement('div');
        message.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #27ae60;
            color: white;
            padding: 15px 25px;
            border-radius: 8px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.3);
            z-index: 10001;
            font-weight: 600;
            max-width: 300px;
        `;
        message.innerHTML = `
            <div style="display: flex; align-items: center;">
                <svg style="width: 20px; height: 20px; margin-right: 10px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="m9 12 2 2 4-4"/>
                    <circle cx="12" cy="12" r="9"/>
                </svg>
                <div>
                    <div>${productData.title} added to samples!</div>
                    <div style="font-size: 12px; opacity: 0.9; margin-top: 5px;">Redirecting to checkout...</div>
                </div>
            </div>
        `;
        
        document.body.appendChild(message);
        
        // Remove message after 3 seconds
        setTimeout(() => {
            if (message.parentNode) {
                message.parentNode.removeChild(message);
            }
        }, 3000);
    }

    // Toggle provider type (for checkboxes)
    static toggleProviderType(type) {
        const checkbox = document.getElementById(`provider_${type}`);
        const option = checkbox.closest('.provider-type-option');
        
        // Toggle checkbox
        checkbox.checked = !checkbox.checked;
        
        // Update visual state
        if (checkbox.checked) {
            option.classList.add('selected');
        } else {
            option.classList.remove('selected');
        }
        
        // Hide error if at least one is selected
        const selectedCount = document.querySelectorAll('input[name="practice_type"]:checked').length;
        if (selectedCount > 0) {
            const errorDiv = document.getElementById('practice-type-error-static');
            if (errorDiv) {
                errorDiv.style.display = 'none';
            }
        }
    }

    // Select sample preference
    static selectSample(preference) {
        // Remove selected class from all options
        document.querySelectorAll('.sample-option').forEach(option => {
            option.classList.remove('selected');
        });
        
        // Add selected class to clicked option
        const selectedOption = document.querySelector(`#samples_${preference.toLowerCase()}`).closest('.sample-option');
        selectedOption.classList.add('selected');
        
        // Check the radio button
        document.getElementById(`samples_${preference.toLowerCase()}`).checked = true;
    }

    // Toggle product interest - REMOVED
    // Product selection is now determined by actual cart contents instead of modal checkboxes
    static toggleProduct(product) {
        console.log('📝 Product interest functionality removed - products determined by cart contents');
    }

    // Simple NPI format validation
    static validateNPI(npi) {
        // Basic validation: exactly 10 digits
        return /^\d{10}$/.test(npi);
    }

    // Get collected provider data
    static getProviderData() {
        const stored = localStorage.getItem('eoseraProviderData');
        return stored ? JSON.parse(stored) : null;
    }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => EoseraProviderModal.init());
} else {
    EoseraProviderModal.init();
}

// Global access
window.EoseraProviderModal = EoseraProviderModal; 